import React, { forwardRef, useImperativeHandle } from 'react';
import {
  CloseCircleOutlined,
  DeleteOutlined,
  FileSearchOutlined,
  FolderOutlined,
  LinkOutlined,
  DragOutlined,
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import { Form } from '@ant-design/compatible';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import ReactCrop from 'react-image-crop';
import InputForm from '../../../../../../../components/form/InputForm';
import SearchEventListModal from './SearchEventListModal';
import useClickLayerUtils from './useClickLayerUtils';
import { formRequiredRules } from '../../../../../../../lib/formRules';
import { REX_URL_AND_APP_SCHEME } from '../../../../../../../lib/rex';
import { EventSectionClickLayer } from '../../../../../../../types/query/event';
import { MAXIMUM_NUMBER_OF_EVENT_CLICK_LINK_URL_TEXT } from '../../../../../../../constants/service/app/eventConstants';
import { ClickLayersStyle } from './style';

interface ClickLayersRef {
  value?: EventSectionClickLayer[];
  onChange?: (clickLayer: EventSectionClickLayer[]) => void;
  onDeleteImage: () => void;
}
interface ClickLayersProps extends FormComponentProps {
  imgUrl: string;
  name: string;
  value?: EventSectionClickLayer[];
  onChange?: (clickLayer: EventSectionClickLayer[]) => void;
  onDeleteImage: () => void;
}
const ClickLayers = forwardRef<ClickLayersRef, ClickLayersProps>((props: ClickLayersProps, ref) => {
  const { form, name, imgUrl, onChange, value = [], onDeleteImage } = props;
  const {
    crop,
    setDrag,
    onCropChange,
    onDeleteMapAreaLinkItem,
    onChangeLinkUrl,
    openSearchProductCategoryModal,
    scrollToMapAreaLinkTop,
    onClickMapAreaLinkInfo,
    eventModalTargetIndex,
    setEventModalTargetIndex,
    onSubmitSearchEvent,
  } = useClickLayerUtils({
    form,
    name,
    value,
    onChange,
  });

  useImperativeHandle(ref, () => ({
    value,
    onChange,
    onDeleteImage,
  }));

  return (
    <ClickLayersStyle>
      <div className="img-wrap" id={`${name}-img-wrap`}>
        <div className="map-box-img">
          <ReactCrop
            src={imgUrl}
            crop={crop}
            onChange={onCropChange}
            onDragStart={() => setDrag(true)}
            onDragEnd={() => setDrag(false)}
            onComplete={() => setDrag(false)}
          />
          {value.map((mapArea, index) => (
            <div
              className="area-link-item"
              key={index}
              id={`${name}-area-link-item-${index}`}
              style={{
                top: mapArea.y + '%',
                left: mapArea.x + '%',
                width: mapArea.width + '%',
                height: mapArea.height + '%',
              }}
            >
              <CloseCircleOutlined onClick={() => onDeleteMapAreaLinkItem(index)} />
              <div className="area-box">{index + 1}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="map-area-list-management">
        <DeleteOutlined className="edit-img-btn" onClick={onDeleteImage} />

        <div className="map-area-list-wrap">
          <h3>링크 영역 리스트</h3>
          {value?.length > 0 ? (
            <>
              <ul className="map-area-list">
                {value.map((mapArea, index) => (
                  <li key={index}>
                    <div className="map-area-info">
                      <span className="map-area-no">{index + 1}</span>
                      <InputForm
                        form={form}
                        isEditable={true}
                        placeholder="ex) 상품상세 : https://www.amondz.com/product/33447"
                        rules={[
                          formRequiredRules(true, 'URL 을 입력하시기 바랍니다.', false),
                          {
                            pattern: REX_URL_AND_APP_SCHEME,
                            message: '올바른 URL 을 입력하시기 바랍니다.',
                          },
                        ]}
                        initialValue={mapArea.url}
                        maxLength={MAXIMUM_NUMBER_OF_EVENT_CLICK_LINK_URL_TEXT}
                        onChange={(url: string) => onChangeLinkUrl(url, index)}
                        name={`${name}-clickLayers-link_${index}`}
                      />

                      <div className="map-area-control">
                        <Tooltip title="카테고리 검색">
                          <FileSearchOutlined onClick={() => openSearchProductCategoryModal(index)} />
                        </Tooltip>
                        <Tooltip title="기획전 검색">
                          <FolderOutlined onClick={() => setEventModalTargetIndex(index)} />
                        </Tooltip>
                        <Tooltip title="해당 링크로 이동">
                          <LinkOutlined onClick={() => scrollToMapAreaLinkTop(index)} />
                        </Tooltip>
                        <Tooltip title="링크 정보 보기">
                          <DragOutlined onClick={() => onClickMapAreaLinkInfo(index)} />
                        </Tooltip>
                        <Tooltip title="링크 삭제">
                          <CloseCircleOutlined onClick={() => onDeleteMapAreaLinkItem(index)} />
                        </Tooltip>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              {eventModalTargetIndex !== null && (
                <SearchEventListModal
                  closeModal={() => setEventModalTargetIndex(null)}
                  onSubmit={onSubmitSearchEvent}
                />
              )}
              <p>
                *응모형 기획전 링크는 "amondz://eventapply/기획전ID" 형식으로 입력해주세요
                <br />
                *기획전ID 앞에 대문자 'P'가 입력된 링크 클릭 시 자동으로 마케팅 푸시 수신 동의됨. 꼭 기획전 유의사항에
                입력 해주세요
              </p>
            </>
          ) : null}
        </div>
      </div>
    </ClickLayersStyle>
  );
});
ClickLayers.displayName = 'ClickLayers';

interface ClickLayerFormProps extends FormComponentProps {
  name: string;
  initialValue?: EventSectionClickLayer[];
  imgUrl: string;
  onDeleteImage: () => void;
}
const ClickLayerForm = (props: ClickLayerFormProps): JSX.Element => {
  const { form, name, initialValue, imgUrl, onDeleteImage } = props;
  const { getFieldDecorator } = form;

  return (
    <div>
      <Form.Item>
        {getFieldDecorator(`${name}-clickLayers`, {
          initialValue,
        })(<ClickLayers form={form} name={name} imgUrl={imgUrl} onDeleteImage={onDeleteImage} />)}
      </Form.Item>
    </div>
  );
};

export default ClickLayerForm;
